<template>
    <div v-if="stateKey !== 'healthy'" class="rounded-lg border overflow-hidden"
        :class="stateKey === 'past-due' ? 'border-red-200' : 'border-blue-200'">
        <div class="px-4 py-3.5 flex items-start gap-3" :class="stateKey === 'past-due' ? 'bg-red-50' : 'bg-blue-50'">
            <div class="w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0"
                :class="stateKey === 'past-due' ? 'border-red-200 bg-white text-red-600' : 'border-blue-200 bg-white text-blue-600'">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                    <path
                        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
            </div>

            <div class="min-w-0 flex-1">
                <div class="flex items-center gap-3 flex-wrap">
                    <p class="text-base font-semibold leading-tight"
                        :class="stateKey === 'past-due' ? 'text-red-700' : 'text-blue-700'">
                        {{ headline }}
                    </p>
                    <span
                        class="inline-flex items-center rounded-full px-2 py-0.5 text-tiny font-semibold uppercase tracking-wide"
                        :class="stateKey === 'past-due' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'">
                        {{ stateKey === 'past-due' ? 'On Hold' : 'Open Balance' }}
                    </span>
                </div>
                <p class="text-xs mt-1.5 max-w-5xl leading-relaxed"
                    :class="stateKey === 'past-due' ? 'text-red-700' : 'text-blue-700'">
                    {{ subline }}
                </p>
            </div>
        </div>

        <div class="bg-white px-4 py-3.5">
            <div v-if="displayInvoices.length" class="space-y-2.5">
                <p class="text-xxs uppercase tracking-wider text-theme-body-light font-semibold">
                    {{ listTitle }}
                    <span class="text-theme-body">&middot; {{ sourceInvoices.length }}</span>
                </p>

                <div class="divide-y divide-theme-border divide-opacity-30">
                    <div v-for="invoice in displayInvoices" :key="invoice.id"
                        class="py-2.5 flex items-start sm:items-center gap-2 justify-between">
                        <div class="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-3 min-w-0 flex-1">
                            <p class="text-theme-secondary text-sm font-medium">
                                {{ invoice.invoice_number || invoice.order_number || invoice.id }}
                            </p>
                            <p class="text-theme-body text-xs truncate">
                                Due {{ formatDate(invoice.due_at) }}
                            </p>
                        </div>

                        <div class="flex items-center gap-3 flex-shrink-0">
                            <span
                                class="inline-flex items-center rounded-full px-2 py-0.5 text-tiny font-semibold uppercase tracking-wide leading-none"
                                :class="invoiceStatusClass(invoice)" :style="invoiceStatusStyle(invoice)">
                                {{ invoiceStatusLabel(invoice) }}
                            </span>
                            <p class="text-theme-secondary text-sm font-semibold w-24 text-right">
                                ${{ currencyFormat(invoice.payable_amount || 0) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-2 flex-wrap pt-0.5">
                    <router-link :to="invoicesPageTo"
                        class="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-theme-secondary text-white text-xs font-semibold hover:bg-opacity-95 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 7h-9" />
                            <path d="M14 17H5" />
                            <circle cx="17" cy="17" r="3" />
                            <circle cx="7" cy="7" r="3" />
                        </svg>
                        <span>{{ primaryActionLabel }}</span>
                    </router-link>

                    <router-link :to="invoicesPageTo"
                        class="inline-flex items-center justify-center px-3.5 py-2 rounded-lg border border-theme-border border-opacity-60 bg-white text-theme-secondary text-xs font-semibold hover:bg-theme-bg-shade hover:bg-opacity-30 transition-colors">
                        View all invoices
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import moment from "moment";
import { computed } from "vue";

const props = defineProps({
    stateKey: { type: String, default: "healthy" },
    isPaid: { type: Boolean, default: false },
    pastDueInvoices: { type: Array, default: () => [] },
    dueSoonInvoices: { type: Array, default: () => [] },
    pastDueTotal: { type: Number, default: 0 },
    openBalanceTotal: { type: Number, default: 0 },
    invoicesPageTo: { type: String, default: "/invoice-payments" },
    currencyFormat: { type: Function, required: true },
});

const sourceInvoices = computed(() => {
    return props.stateKey === "past-due"
        ? props.pastDueInvoices
        : props.dueSoonInvoices;
});

const displayInvoices = computed(() => {
    return [...sourceInvoices.value]
        .sort((a, b) => moment(a.due_at).valueOf() - moment(b.due_at).valueOf())
        .slice(0, 5);
});

const headline = computed(() => {
    if (props.stateKey === "past-due") {
        return props.isPaid
            ? "Past-due balance on account"
            : "Order is on hold due to past-due balance";
    }
    return "Open invoice balance";
});

const subline = computed(() => {
    if (props.stateKey === "past-due") {
        const total = `$${props.currencyFormat(props.pastDueTotal || 0)}`;
        if (props.isPaid) {
            return `${total} is currently past due. This order is not blocked because payment is already completed.`;
        }
        return `We've got your order and items are reserved. It ships once ${total} in past-due invoices is cleared. Orders are released as soon as payment is received.`;
    }

    return `You have $${props.currencyFormat(props.openBalanceTotal || 0)} in open invoices with no past due balance.`;
});

const listTitle = computed(() => {
    return props.stateKey === "past-due" ? "Past Due Invoices" : "Invoices Due Soon";
});

const primaryActionLabel = computed(() => {
    if (props.stateKey === "past-due") {
        return `Pay $${props.currencyFormat(props.pastDueTotal || 0)} now`;
    }
    return "Pay invoices";
});

const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("MMM D, YYYY");
};

const lateLabel = (invoice) => {
    const days = Math.max(moment().startOf("day").diff(moment(invoice.due_at).startOf("day"), "days"), 0);
    return `${days} DAY${days === 1 ? "" : "S"} LATE`;
};

const dueLabel = (invoice) => {
    const days = Math.max(moment(invoice.due_at).startOf("day").diff(moment().startOf("day"), "days"), 0);
    if (days === 0) return "DUE TODAY";
    return `DUE IN ${days} DAY${days === 1 ? "" : "S"}`;
};

const invoiceStatusLabel = (invoice) => {
    return props.stateKey === "past-due" ? lateLabel(invoice) : dueLabel(invoice);
};

const invoiceStatusClass = () => {
    if (props.stateKey === "past-due") return "bg-red-50 text-red-700";
    return "";
};

const invoiceStatusStyle = () => {
    if (props.stateKey === "past-due") return {};
    return {
        backgroundColor: "#F5EDE3",
        color: "#6B4E2E",
    };
};
</script>
